import {
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Divider,
} from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { OfertasContext } from '../../context/ofertas-context';
import { capitalizeSentence } from '../../helpers/ofertas-helper';
import { FilterTypes } from '../../services/ofertas-service/models/ofertas';
import { useTrackingManager } from '../../tracking/use-tracking-manager';

export const DesktopSelector: React.FC<{
	handleFilteredCarlines: (selectedButton: string) => void;
	selectedGamaFilter: string | null;
	selectedMotorizacionFilter: string | null;
	setSelectedGamaFilter: React.Dispatch<React.SetStateAction<string | null>>;
	setSelectedMotorizacionFilter: React.Dispatch<
		React.SetStateAction<string | null>
	>;
	setActiveCarlineIndex: (index: number) => void;
}> = ({
	handleFilteredCarlines,
	selectedGamaFilter,
	selectedMotorizacionFilter,
	setSelectedGamaFilter,
	setSelectedMotorizacionFilter,
	setActiveCarlineIndex,
}) => {
	const { ofertasService } = React.useContext(OfertasContext);

	const [currentIndex, setCurrentIndex] = useState(0);

	const trackingManager = useTrackingManager();

	const motorizacionTypes = [
		FilterTypes.TODOS,
		FilterTypes.ELECTRICOS,
		FilterTypes.HIBRIDOS_ENCHUFABLES,
		FilterTypes.COMBUSTION,
	];

	const gamaTypes = [FilterTypes.PROFESIONAL, FilterTypes.LIFESTYLE];

	const shouldShowMotorizacionButtons = () => {
		return (
			[
				ofertasService.checkFilterExists(FilterTypes.ELECTRICOS),
				ofertasService.checkFilterExists(FilterTypes.HIBRIDOS_ENCHUFABLES),
				ofertasService.checkFilterExists(FilterTypes.COMBUSTION),
			].filter(Boolean).length > 1
		);
	};

	const shouldShowGamaButtons = () => {
		return (
			[
				ofertasService.checkFilterExists(FilterTypes.PROFESIONAL),
				ofertasService.checkFilterExists(FilterTypes.LIFESTYLE),
			].filter(Boolean).length > 1
		);
	};

	const showSelectorOptions = () => {
		return shouldShowMotorizacionButtons() || shouldShowGamaButtons();
	};

	const shouldShowTodosButton = () => {
		return shouldShowMotorizacionButtons() || shouldShowGamaButtons();
	};

	const handleOnClick = (selector: string, index?: number) => {
		setActiveCarlineIndex(0);

		if (selector === FilterTypes.TODOS) {
			setSelectedGamaFilter(null);
			setSelectedMotorizacionFilter(null);
		} else if (gamaTypes.includes(selector as FilterTypes)) {
			setSelectedGamaFilter(selector);
		} else if (motorizacionTypes.includes(selector as FilterTypes)) {
			setSelectedMotorizacionFilter(selector);
		}

		trackingManager.trackOfertasFilterSelectClick({
			type: 'single_value',
			name: 'FT',
			values: [selector],
		});

		handleFilteredCarlines(selector);

		if (index) {
			setCurrentIndex(index);
		}
	};

	useEffect(() => {
		const scrollContainer = document.getElementById('scroll-filter-menu');

		const allFilters = [...motorizacionTypes, ...gamaTypes];
		const activeFilter = selectedGamaFilter || selectedMotorizacionFilter;
		const listPosition = activeFilter
			? allFilters.indexOf(activeFilter as FilterTypes)
			: 0;

		let scrollAmount = 20;

		switch (listPosition) {
			case 1:
				scrollAmount = 10;
				break;
			case 2:
				scrollAmount = 100;
				break;
			case 3:
				scrollAmount = 130;
				break;
			case 4:
				scrollAmount = 160;
				break;
			case 5:
				scrollAmount = 190;
				break;
			default:
				break;
		}

		scrollContainer &&
			scrollContainer.scroll({
				left: scrollAmount,
				behavior: 'smooth',
			});
	}, [currentIndex, selectedGamaFilter, selectedMotorizacionFilter]);

	return (
		<Container
			data-testid="filter-selector"
			padding={{
				top: ContainerPadding.dynamic0150,
			}}
		>
			{showSelectorOptions() && (
				<Container wrap={'never'} gutter={ContainerGutter.static200}>
					{shouldShowTodosButton() && (
						<CTA
							tag="button"
							emphasis={
								!selectedGamaFilter && !selectedMotorizacionFilter
									? 'primary'
									: 'secondary'
							}
							size={'small'}
							onClick={() => handleOnClick(motorizacionTypes[0])}
						>
							{motorizacionTypes[0]}
						</CTA>
					)}

					{shouldShowGamaButtons() && (
						<Container wrap={'never'} gutter={ContainerGutter.static200}>
							<Divider isVertical />
							{ofertasService.checkFilterExists(FilterTypes.PROFESIONAL) && (
								<CTA
									tag="button"
									emphasis={
										selectedGamaFilter === gamaTypes[0]
											? 'primary'
											: 'secondary'
									}
									size={'small'}
									disabled={
										!ofertasService.checkFilterAvailable(
											gamaTypes[0],
											selectedGamaFilter,
											selectedMotorizacionFilter,
										)
									}
									onClick={() => handleOnClick(gamaTypes[0])}
								>
									{capitalizeSentence(gamaTypes[0])}
								</CTA>
							)}
							{ofertasService.checkFilterExists(FilterTypes.LIFESTYLE) && (
								<CTA
									tag="button"
									emphasis={
										selectedGamaFilter === gamaTypes[1]
											? 'primary'
											: 'secondary'
									}
									size={'small'}
									disabled={
										!ofertasService.checkFilterAvailable(
											gamaTypes[1],
											selectedGamaFilter,
											selectedMotorizacionFilter,
										)
									}
									onClick={() => handleOnClick(gamaTypes[1])}
								>
									{capitalizeSentence(gamaTypes[1])}
								</CTA>
							)}
							<Divider isVertical />
						</Container>
					)}

					{shouldShowMotorizacionButtons() &&
						ofertasService.checkFilterExists(FilterTypes.ELECTRICOS) && (
							<CTA
								tag="button"
								emphasis={
									selectedMotorizacionFilter === motorizacionTypes[1]
										? 'primary'
										: 'secondary'
								}
								size={'small'}
								disabled={
									!ofertasService.checkFilterAvailable(
										FilterTypes.ELECTRICOS,
										selectedGamaFilter,
										selectedMotorizacionFilter,
									)
								}
								onClick={() => handleOnClick(motorizacionTypes[1])}
							>
								{motorizacionTypes[1]}
							</CTA>
						)}

					{shouldShowMotorizacionButtons() &&
						ofertasService.checkFilterExists(
							FilterTypes.HIBRIDOS_ENCHUFABLES,
						) && (
							<CTA
								tag="button"
								emphasis={
									selectedMotorizacionFilter === motorizacionTypes[2]
										? 'primary'
										: 'secondary'
								}
								size={'small'}
								disabled={
									!ofertasService.checkFilterAvailable(
										FilterTypes.HIBRIDOS_ENCHUFABLES,
										selectedGamaFilter,
										selectedMotorizacionFilter,
									)
								}
								onClick={() => handleOnClick(motorizacionTypes[2])}
							>
								{motorizacionTypes[2]}
							</CTA>
						)}

					{shouldShowMotorizacionButtons() &&
						ofertasService.checkFilterExists(FilterTypes.COMBUSTION) && (
							<CTA
								tag="button"
								emphasis={
									selectedMotorizacionFilter === motorizacionTypes[3]
										? 'primary'
										: 'secondary'
								}
								size={'small'}
								disabled={
									!ofertasService.checkFilterAvailable(
										FilterTypes.COMBUSTION,
										selectedGamaFilter,
										selectedMotorizacionFilter,
									)
								}
								onClick={() => handleOnClick(motorizacionTypes[3])}
							>
								{motorizacionTypes[3]}
							</CTA>
						)}
				</Container>
			)}
		</Container>
	);
};
